import React, { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { simulerSalaire, fmtEUR } from "@/lib/salaireSimulator";
import { useBareme } from "@/hooks/useBareme";
import { Calculator, TrendingDown, Wallet, Building2 } from "lucide-react";

const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("fr-FR") : "—");

export default function SalaireSimulator({ defaultBrut = 2500 }) {
  const [brut, setBrut] = useState(defaultBrut);
  const [statut, setStatut] = useState("non_cadre");
  const [parts, setParts] = useState(1);
  const { values: bareme, meta } = useBareme();

  const res = useMemo(() => simulerSalaire({ brut_mensuel: brut, statut, parts, bareme }), [brut, statut, parts, bareme]);

  return (
    <div className="grid lg:grid-cols-12 gap-6">
      {/* Form */}
      <div className="lg:col-span-5 bg-white border border-[#DEE2E6] p-6 lg:p-8 space-y-5" data-testid="simulator-form">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-10 w-10 bg-[#002FA7] flex items-center justify-center">
            <Calculator className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#002FA7] font-bold">Simulateur</div>
            <div className="font-display text-lg font-black tracking-tight">Brut → Net</div>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Salaire brut mensuel</Label>
          <div className="relative">
            <Input type="number" value={brut} onChange={(e) => setBrut(parseFloat(e.target.value) || 0)} data-testid="sim-brut" className="h-12 text-lg font-bold pr-10" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#868E96] font-semibold">€</span>
          </div>
          <div className="text-[10px] text-[#868E96] mt-1">SMIC mensuel ≈ {fmtEUR(res.smic_mensuel)}</div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Statut</Label>
          <Select value={statut} onValueChange={setStatut}>
            <SelectTrigger data-testid="sim-statut" className="h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="non_cadre">Non-cadre</SelectItem>
              <SelectItem value="cadre">Cadre</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Nombre de parts fiscales</Label>
          <Input type="number" step="0.5" min={1} value={parts} onChange={(e) => setParts(parseFloat(e.target.value) || 1)} data-testid="sim-parts" className="h-11" />
          <div className="text-[10px] text-[#868E96] mt-1">1 = célibataire · 2 = couple sans enfant · 2.5 = couple + 1 enfant</div>
        </div>

        <div className="pt-4 border-t border-[#DEE2E6] text-[11px] text-[#868E96] leading-relaxed">
          <div data-testid="sim-bareme">
            Barème appliqué : {meta ? <>version en vigueur au <b>{fmtDate(meta.at)}</b> · {meta.rules_count} paramètres · dernière mise à jour le <b>{fmtDate(meta.latest_effective_from)}</b></> : "valeurs de repli 2026"}
          </div>
          <div className="mt-1">⚠️ Calcul indicatif (URSSAF, AGIRC-ARRCO, CSG-CRDS, IR simplifiés). Les montants réels dépendent de votre convention collective, mutuelle, prévoyance et avantages.</div>
        </div>
      </div>

      {/* Results */}
      <div className="lg:col-span-7 space-y-3" data-testid="simulator-results">
        <div className="grid grid-cols-3 gap-0 border border-[#0A0A0A]">
          <Big value={fmtEUR(res.brut)} label="Brut mensuel" sub="versé par l'employeur" accent="#0A0A0A" testid="res-brut" />
          <Big value={fmtEUR(res.net_avant_impot)} label="Net avant impôt" sub="versé sur compte" accent="#002FA7" highlight testid="res-net" />
          <Big value={fmtEUR(res.net_apres_ir)} label="Net après IR" sub="approximation prélèvement" accent="#059669" testid="res-net-ir" />
        </div>

        <div className="bg-white border border-[#DEE2E6] p-5">
          <div className="text-xs uppercase tracking-[0.25em] font-bold mb-3 flex items-center gap-2">
            <TrendingDown className="h-4 w-4 text-[#E60000]" /> Cotisations salariales
          </div>
          <div className="space-y-1.5 text-sm">
            {res.lines.map((l, i) => l.montant > 0 ? (
              <div key={i} className="flex justify-between border-b border-[#F8F9FA] pb-1">
                <span className="text-[#495057]">{l.label} <span className="text-[10px] text-[#868E96]">({(l.rate * 100).toFixed(2)}%)</span></span>
                <span className="font-mono font-semibold">- {fmtEUR(l.montant)}</span>
              </div>
            ) : null)}
            <div className="flex justify-between pt-2 font-bold">
              <span>Total charges salariales</span>
              <span className="font-mono">- {fmtEUR(res.total_cotisations_salariales)}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="bg-[#F8F9FA] border border-[#DEE2E6] p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#868E96] font-semibold flex items-center gap-2"><Wallet className="h-3 w-3" /> Net imposable</div>
            <div className="font-display text-2xl font-black mt-1">{fmtEUR(res.net_imposable)}</div>
            <div className="text-[10px] text-[#495057] mt-1">IR mensuel estimé : <b>{fmtEUR(res.impot_revenu_mensuel)}</b></div>
          </div>
          <div className="bg-[#FFF8E1] border border-[#FFD700] p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#868E96] font-semibold flex items-center gap-2"><Building2 className="h-3 w-3" /> Coût employeur</div>
            <div className="font-display text-2xl font-black mt-1">{fmtEUR(res.cout_employeur)}</div>
            <div className="text-[10px] text-[#495057] mt-1">Charges patronales : <b>{fmtEUR(res.charges_patronales)}</b></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Big({ value, label, sub, accent, highlight, testid }) {
  return (
    <div data-testid={testid} className={`p-5 lg:p-6 ${highlight ? "bg-[#002FA7] text-white" : "bg-white"} border-r border-[#0A0A0A] last:border-r-0`}>
      <div className="text-[10px] uppercase tracking-[0.25em] font-bold opacity-70" style={!highlight ? { color: accent } : {}}>{label}</div>
      <div className="font-display text-2xl lg:text-3xl font-black tracking-tighter mt-1.5">{value}</div>
      <div className="text-[10px] opacity-60 mt-1">{sub}</div>
    </div>
  );
}
